import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface TourState {
  // Which user the persisted progress below belongs to. `wf-tour` is a single
  // browser-wide key, so without an owner the next account to sign in on this
  // browser inherits a finished tour and never sees its own (lib/auth-reset.ts).
  ownerUserId: string | null;
  completed: string[];
  autoStarted: boolean;
  activeTourId: string | null;
  stepIndex: number;
  startTour: (id: string) => void;
  nextStep: () => void;
  prevStep: () => void;
  endTour: (markCompleted: boolean) => void;
  markSeen: (id: string) => void;
  setAutoStarted: (value: boolean) => void;
  claimForUser: (userId: string) => void;
}

export const useTourStore = create<TourState>()(
  persist(
    (set, get) => ({
      ownerUserId: null,
      completed: [],
      autoStarted: false,
      activeTourId: null,
      stepIndex: 0,
      startTour: (id) => set({ activeTourId: id, stepIndex: 0 }),
      nextStep: () => set({ stepIndex: get().stepIndex + 1 }),
      prevStep: () => set({ stepIndex: Math.max(0, get().stepIndex - 1) }),
      endTour: (markCompleted) => {
        const { activeTourId, completed } = get();
        set({
          activeTourId: null,
          stepIndex: 0,
          completed: markCompleted && activeTourId && !completed.includes(activeTourId) ? [...completed, activeTourId] : completed,
        });
      },
      // Records a tour as seen without running it to the end. Auto-started page
      // tours call this the moment they open, so a tour a user skips two steps
      // in does not reopen every time they come back to the page.
      markSeen: (id) => {
        const { completed } = get();
        if (!completed.includes(id)) set({ completed: [...completed, id] });
      },
      setAutoStarted: (value) => set({ autoStarted: value }),
      // Wipes progress when a different account takes over this browser. Signing
      // out and back in as the same user is a no-op, so a finished tour stays
      // finished.
      claimForUser: (userId) => {
        if (get().ownerUserId === userId) return;
        set({ ownerUserId: userId, completed: [], autoStarted: false, activeTourId: null, stepIndex: 0 });
      },
    }),
    {
      name: 'wf-tour',
      partialize: (state) => ({ ownerUserId: state.ownerUserId, completed: state.completed, autoStarted: state.autoStarted }),
    },
  ),
);
